import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from "recharts";
import { summaryCardTheme as t } from "./styles";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";

function SummaryFrameworkComplianceChart({ overall, framework, onFrameworkChange }) {
  const data = useMemo(() => {
    const list = Array.isArray(overall?.compliance_by_framework)
      ? overall.compliance_by_framework
      : [];

    return list
      .filter((fw) => (fw.controls_failed_total ?? 0) > 0)
      .map((fw) => ({
        name: fw.framework || "—",
        arns: fw.arns_failed_total ?? 0,
        controls: fw.controls_failed_total ?? 0,
      }))
      .sort((a, b) => b.controls - a.controls);
  }, [overall]);

  const sliceColors = useMemo(() => {
    const base = t.colors.chart || [];
    const fallback = ["#6366F1", "#F97316", "#22C55E", "#EC4899", "#06B6D4", "#FACC15"];
    return data.map((_, idx) => base[idx % base.length] || fallback[idx % fallback.length]);
  }, [data]);

  const CustomYAxisTick = ({ x, y, payload }) => {
    const name = payload?.value;
    const iconSrc = name ? getComplianceIcon(name) : null;

    // Render logos in the Y-axis to avoid long framework names pushing/obscuring tooltips.
    return (
      <g transform={`translate(${x},${y})`}>
        {iconSrc ? (
          <image
            href={iconSrc}
            x={-18}
            y={-9}
            width={18}
            height={18}
            preserveAspectRatio="xMidYMid meet"
          />
        ) : (
          <text
            x={0}
            y={0}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={10}
            fill={t.colors.text}
          >
            —
          </text>
        )}
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;

    const iconSrc = getComplianceIcon(d.name);

    // Hover-only: show brief metrics so tooltip can disappear without affecting interaction.
    return (
      <Box
        sx={{
          ...t.chartTooltip,
          // Prevent horizontal overflow; adapt to screen size.
          maxWidth: "min(260px, 80vw)",
          p: 1.25,
          whiteSpace: "normal",
          wordBreak: "break-word",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.25,
          }}
        >
          {iconSrc && (
            <Box
              component="img"
              src={iconSrc}
              alt={d.name}
              sx={{
                width: 18,
                height: 18,
                borderRadius: 1,
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          )}
          <Typography component="span" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.78rem" }}>
            {d.name}
          </Typography>
        </Box>
        <Typography component="div" sx={{ color: t.colors.textMuted, fontSize: "0.73rem" }}>
          {d.arns} ARNs with findings · {d.controls} failed controls
        </Typography>
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No compliance framework findings
        </Typography>
      </Box>
    );
  }

  const needsScroll = data.length > 5;
  const scrollInnerHeight = Math.max(240, 28 + data.length * 42);
  const barMax = data.length <= 3 ? 36 : data.length <= 5 ? 28 : 18;
  const categoryGap = data.length <= 3 ? "10%" : data.length <= 5 ? "8%" : "6%";

  const chartBody = (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 12, left: 22, bottom: 32 }}
        barCategoryGap={categoryGap}
      >
        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={t.colors.borderLight} />
        <XAxis
          type="number"
          tick={{ fontSize: 10, fill: t.colors.textMuted }}
          tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
          label={{
            value: "Failed controls",
            position: "bottom",
            offset: 4,
            style: { fill: t.colors.textMuted, fontSize: 11, fontWeight: 500, fontFamily: "system-ui, sans-serif" },
          }}
        />
        <YAxis type="category" dataKey="name" width={56} tick={<CustomYAxisTick />} interval={0} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: t.colors.background }} />
        <Bar
          dataKey="controls"
          radius={[0, 4, 4, 0]}
          maxBarSize={barMax}
          onClick={(entry) => {
            const clickedName = entry?.name;
            if (!clickedName || !onFrameworkChange) return;
            onFrameworkChange(framework && framework === clickedName ? "" : clickedName);
          }}
        >
          {data.map((entry, index) => {
            const hasSel = Boolean(framework);
            const isActive = framework && framework === entry.name;
            return (
              <Cell
                key={`bar-${entry.name}-${index}`}
                fill={sliceColors[index]}
                fillOpacity={hasSel ? (isActive ? 1 : 0.22) : 1}
                stroke={isActive ? t.colors.accent : "transparent"}
                strokeWidth={isActive ? 2.5 : 0}
                style={
                  hasSel
                    ? isActive
                      ? { filter: "drop-shadow(0 0 10px rgba(59,130,246,0.45))" }
                      : { filter: "blur(1.2px)" }
                    : undefined
                }
              />
            );
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        height: "100%",
        minHeight: 280,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: t.colors.text,
          fontSize: "0.875rem",
          mb: 1,
          flexShrink: 0,
        }}
      >
        Compliance Frameworks
      </Typography>
      {needsScroll ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 200,
            maxHeight: 280,
            overflowY: "auto",
            overflowX: "hidden",
            pr: 0.5,
            mt: 0.5,
          }}
        >
          <Box sx={{ width: "100%", height: scrollInnerHeight }}>
            {chartBody}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 200,
            width: "100%",
            position: "relative",
            mt: 0.5,
          }}
        >
          {chartBody}
        </Box>
      )}
    </Box>
  );
}

export default SummaryFrameworkComplianceChart;

